/**
 * ExecHome — RRM executive reporting.
 *
 * Layout per `rrm-exec-home-volume.html`:
 *   - Header (title + period tabs + filter chips + export actions)
 *   - Category tabs (Volume / Velocity / Mix / Channel / Quality / Customer / Operational / Financial / Innovation)
 *   - Tab body
 *
 * Role-gate (tightened 2026-04-21 per rrm-module-v3.md spec + audit H-RRM-6):
 *   RRM is for **CEO / CFO / Innovation Officer (Bill) only**. Managers get RPM,
 *   not RRM. SuperAdmin retains access for ops support. Innovation tab is
 *   hidden unless the caller is Bill (Innovation Officer) per audit H-RRM-7.
 */

import { useMemo, useState } from 'react';
import { Pill, Button } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { RrmCategory, RrmFilters, RrmPeriod } from './types';
import { VolumeTab } from './VolumeTab';
import { VelocityTab } from './VelocityTab';
import { MixTab } from './MixTab';
import { ChannelTab } from './ChannelTab';
import { InnovationTab } from './InnovationTab';
import { DateRangePicker } from './components/DateRangePicker';
import { BoardPacket } from './components/BoardPacket';

/**
 * Innovation Officer UPN allowlist. The spec names Bill Wiggins as the sole
 * Innovation Officer at launch. We match by explicit UPN rather than a
 * dedicated role so Bill retains access whether his role is tagged
 * 'Executive', 'SuperAdmin', or a future 'Innovation' role.
 */
const INNOVATION_OFFICER_EMAILS: ReadonlySet<string> = new Set([
  'abwiggins@mypcb.com',
  'bwiggins@pcbmo.com',
  'wewg24@gmail.com',
]);

function isInnovationOfficer(user: { upn?: string } | null | undefined): boolean {
  if (!user) return false;
  const upn = (user.upn ?? '').toLowerCase();
  if (upn && INNOVATION_OFFICER_EMAILS.has(upn)) return true;
  // Legacy substring fallback (matches pre-rollout pilot accounts).
  return upn.includes('wiggins') || upn.includes('wewg24');
}

export function ExecHome(): JSX.Element {
  const { user } = useAuth();
  const { config } = useConfig();
  const [cat, setCat] = useState<RrmCategory>('volume');
  const [filters, setFilters] = useState<RrmFilters>({
    period: 'month',
    custom: null,
    branchCode: 'all',
    productCode: 'all',
  });

  // RRM is scoped to CEO / CFO / Innovation Officer + SuperAdmin (ops support).
  // Managers explicitly do NOT get access — they use RPM instead.
  const billAccess = isInnovationOfficer(user ?? null);
  const isExec =
    user?.primaryRole === 'Executive' ||
    user?.primaryRole === 'SuperAdmin' ||
    billAccess;

  const canSeeInnovation = billAccess;

  const branchOptions = useMemo(
    () => [
      { value: 'all', label: 'All Branches' },
      ...config.branches.map((b) => ({ value: b.branchCode, label: b.branchName })),
    ],
    [config.branches],
  );

  if (!isExec) {
    return (
      <div className="module-page">
        <header className="module-page__header">
          <h1>RRM · Executive Reporting</h1>
        </header>
        <p style={{ padding: 'var(--sp-9)', color: 'var(--ink-600)' }}>
          This view is role-gated to CEO / CFO / Innovation Officer only.
          Managers should use RPM (Retail Performance Metrics) for daily ops.
        </p>
      </div>
    );
  }

  return (
    <div className="module-page">
      <div className="rrm-header">
        <div className="rrm-header__top">
          <div>
            <h1>Retail Reporting Metrics</h1>
            <div className="rrm-header__sub">
              Executive &amp; strategic reporting · CEO / CFO / Innovation ·
              Last updated: daily MetricsDaily batch · role = {user?.primaryRole}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-4)' }}>
            <Button variant="ghost" size="sm">📅 Schedule delivery</Button>
            <Button variant="ghost" size="sm">⬇ Per-tile PDF</Button>
            <Button variant="green" size="sm">📦 Export Full Packet</Button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)', flexWrap: 'wrap' }}>
          <div className="rrm-period-tabs" role="tablist" aria-label="Reporting period">
            {(['week', 'month', 'quarter', 'year', 'rolling12', 'custom'] as const).map((p) => (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={filters.period === p}
                className={`rrm-period-tab ${filters.period === p ? (cat === 'innovation' ? 'is-active-innov' : 'is-active') : ''}`.trim()}
                onClick={() => setFilters({ ...filters, period: p as RrmPeriod })}
              >
                {p === 'rolling12' ? 'Rolling 12mo' : p === 'custom' ? 'Custom…' : p[0]?.toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          {filters.period === 'custom' && (
            <DateRangePicker
              value={filters.custom}
              onChange={(range) => setFilters({ ...filters, custom: range })}
            />
          )}
          <select
            value={filters.branchCode}
            onChange={(e) => setFilters({ ...filters, branchCode: e.target.value })}
            style={{ padding: 'var(--sp-3) var(--sp-6)', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-full)', fontFamily: 'inherit', fontSize: 12 }}
            aria-label="Branch filter"
          >
            {branchOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="rrm-cat-tabs" role="tablist" aria-label="Reporting categories">
        {(['volume', 'velocity', 'mix', 'channel', 'quality', 'customer', 'operational', 'financial'] as const).map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={cat === c}
            className={`rrm-cat-tab ${cat === c ? 'is-active' : ''}`.trim()}
            onClick={() => setCat(c)}
          >
            {c[0]?.toUpperCase()}{c.slice(1)}
          </button>
        ))}
        {canSeeInnovation && (
          <button
            type="button"
            role="tab"
            aria-selected={cat === 'innovation'}
            className={`rrm-cat-tab is-innovation ${cat === 'innovation' ? 'is-active' : ''}`.trim()}
            onClick={() => setCat('innovation')}
          >
            💡 Innovation Tab
          </button>
        )}
      </div>

      {cat === 'volume' && <VolumeTab filters={filters} />}
      {cat === 'velocity' && <VelocityTab filters={filters} />}
      {cat === 'mix' && <MixTab filters={filters} />}
      {cat === 'channel' && <ChannelTab filters={filters} />}
      {cat === 'quality' && <PlaceholderTab title="Quality" subtitle="Compliance exceptions, QC re-work rates, SAR/CTR counts, OFAC review queue." />}
      {cat === 'customer' && <PlaceholderTab title="Customer" subtitle="New customers, retention, NPS proxies, CIF growth." />}
      {cat === 'operational' && <PlaceholderTab title="Operational" subtitle="Staff utilization, SLA compliance, escalation rates, per-branch throughput." />}
      {cat === 'financial' && <PlaceholderTab title="Financial" subtitle="Fee income, NII, cost-to-open, $/account opened." />}
      {cat === 'innovation' && canSeeInnovation && <InnovationTab filters={filters} />}

      <section style={{ marginTop: 'var(--sp-10)' }}>
        <BoardPacket
          period={filters.period}
          onExport={() => { /* TODO wire to /v3/audit-reports/board-packet */ }}
        />
      </section>
    </div>
  );
}

function PlaceholderTab({ title, subtitle }: { title: string; subtitle: string }): JSX.Element {
  return (
    <div style={{ padding: 'var(--sp-11)', background: 'var(--surface-card)', borderRadius: 'var(--r-lg)', border: '1px solid var(--surface-border)' }}>
      <h2 style={{ color: 'var(--pcb-blue-dark)', marginBottom: 'var(--sp-5)' }}>{title}</h2>
      <p style={{ color: 'var(--ink-600)', marginBottom: 'var(--sp-6)' }}>{subtitle}</p>
      <Pill variant="info">Wave 3 · wiring to MetricsDaily batch</Pill>
    </div>
  );
}
